/**
 * @file pickup-locations.ts
 *
 * Pickup-location API for the shop admin, wired to the real commerce-engine `/v1`
 * shipping pickup-locations endpoints. Maps the `/v1` {@link PickupLocationResponseV1}
 * onto the design-reviewed {@link ShopPickupLocation} (id-based, with `sortOrder`) the
 * Local-pickup section consumes, and exposes the CRUD + drag-reorder mutations. This is
 * the only place that knows the backend's specifics, so the store and screens stay
 * decoupled from the wire shape.
 */

import { v1Get, v1Mutate } from './v1-request';
import type { PickupLocationInput, ShopPickupLocation } from './shop-types';
import type {
  PickupLocationResponseV1,
  PickupLocationWriteV1,
  ReorderPickupLocationsRequestV1,
} from './v1-types';

/** Maps a `/v1` pickup location onto the id-based {@link ShopPickupLocation}. */
export function toShopPickupLocation(p: PickupLocationResponseV1): ShopPickupLocation {
  return {
    id: p.id,
    name: p.name,
    address: {
      address_1: p.addressLine1 ?? '',
      city: p.city ?? '',
      state: p.state ?? '',
      postcode: p.postcode ?? '',
      country: p.country ?? '',
    },
    details: p.instructions ?? '',
    sortOrder: p.sortOrder,
  };
}

/** Maps the section/form input onto the `/v1` write body (empty strings become `null`). */
function toWriteBody(input: PickupLocationInput): PickupLocationWriteV1 {
  return {
    name: input.name,
    addressLine1: input.address.address_1 || null,
    city: input.address.city || null,
    state: input.address.state || null,
    postcode: input.address.postcode || null,
    country: input.address.country || null,
    instructions: input.details || null,
  };
}

/**
 * Lists the shop's pickup locations in display order.
 *
 * @returns The pickup locations.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function fetchPickupLocations(): Promise<ShopPickupLocation[]> {
  const res = await v1Get<PickupLocationResponseV1[]>('/shipping/pickup-locations');
  return res.data.map(toShopPickupLocation);
}

/**
 * Creates a pickup location at the given display position.
 *
 * @param input - The editable fields.
 * @param sortOrder - The display position (the section appends at the end).
 *
 * @returns The created pickup location.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function createPickupLocation(
  input: PickupLocationInput,
  sortOrder: number,
): Promise<ShopPickupLocation> {
  const body: PickupLocationWriteV1 = { ...toWriteBody(input), sortOrder };
  const res = await v1Mutate<PickupLocationResponseV1>('/shipping/pickup-locations', 'POST', body);
  return toShopPickupLocation(res);
}

/**
 * Updates a pickup location's editable fields (its position is unchanged here; the
 * drag-reorder uses {@link reorderPickupLocations}).
 *
 * @param id - The pickup-location id.
 * @param input - The editable fields.
 *
 * @returns The updated pickup location.
 *
 * @throws {WcApiError} When the request fails (e.g. a 404 for an unknown id).
 */
export async function updatePickupLocation(
  id: string,
  input: PickupLocationInput,
): Promise<ShopPickupLocation> {
  const res = await v1Mutate<PickupLocationResponseV1>(
    `/shipping/pickup-locations/${id}`,
    'PATCH',
    toWriteBody(input),
  );
  return toShopPickupLocation(res);
}

/**
 * Soft-deletes a pickup location.
 *
 * @param id - The pickup-location id.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function deletePickupLocation(id: string): Promise<void> {
  await v1Mutate(`/shipping/pickup-locations/${id}`, 'DELETE');
}

/**
 * Reorders the pickup locations in one bulk call (the drag-reorder).
 *
 * @param order - The `(id, sortOrder)` pairs in the desired display order.
 *
 * @throws {WcApiError} When the request fails.
 */
export async function reorderPickupLocations(
  order: ReorderPickupLocationsRequestV1['order'],
): Promise<void> {
  await v1Mutate<PickupLocationResponseV1[]>(
    '/shipping/pickup-locations/reorder',
    'POST',
    { order },
  );
}
